import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Search, Plus, Eye, Edit, Trash2, MoreVertical, UserCog, X, Link2 } from 'lucide-react';
import useApi from "../../api_service/useApi";
import LoadingStateComp from "@src/components/LoadingComponent/LoadingComponent";
import Pagination from "@src/components/Pagination/Pagination";
import { cn } from "@src/utils/app_functions";

interface MigrateSystemAdmin {
  email: string;
  fullname: string;
  systemAppId: string;
}

interface AccessLevel {
  _id?: string;
  levelName?: string;
  status?: string;
  allowedEndpoints?: any[];
  rateLimit?: number;
  canResetAdminPassword?: boolean;
  canManageAdmin?: boolean;
  canManageTeamAdmins?: boolean;
  canManageTransactions?: boolean;
  canUpdateTransaction?: boolean;
  canManageAffilliates?: boolean;
  canManageUsers?: boolean;
  canManageTickets?: boolean;
  canManageResources?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  __v?: number;
  isSuperAdmin?: boolean;
}

interface SystemConfig {
  appname?: string;
  type?: string;
  domain?: string;
  requiresSecretKey?: boolean;
  showPricing?: boolean;
  exclusiveUsers?: boolean;
  accessType?: string;
}

interface SchoolAIData {
  _id?: string;
  systemAppId?: string;
  name?: string;
  email?: string;
  city?: string;
  agreeToUpdates?: boolean;
  organizationName?: string;
  accessLevel?: AccessLevel;
  sessionTimeout?: number;
  systemConfig?: SystemConfig;
  theme?: string;
  defaultLanguage?: string;
  notifications?: any[];
  typeOfOrganization?: string;
  status?: string;
  createdAt?: Date;
  updatedAt?: Date;
  appID?: string;
  __v?: number;
  lastLogin?: Date;
  domain?: string;
  appLogo?: string;
  adminSwitchedBy: string | null | Record<string, unknown>;
}

type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

const SchoolAIPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [schools, setSchools] = useState<SchoolAIData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [migrateModal, setMigrateModal] = useState<{ open: boolean; school: SchoolAIData | null }>({ open: false, school: null });
  const [migrateForm, setMigrateForm] = useState({ fullname: '', email: '' });
  const [migrateLoading, setMigrateLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const path = window.location.pathname;

  const { loading, error, get, post } = useApi();

  const {
    setSchoolAdminPageName,
    setShowInput,
    setMenu,
    setIsAffiliate,
  } = useOutletContext<LayoutContextType>();

  useEffect(() => {
    setSchoolAdminPageName("School AI");
    setShowInput(false);
    setMenu(undefined);
    setIsAffiliate(false);
  }, [path]);

  const fetchSchools = useCallback(async (
    query = '',
    page: number = currentPage,
    showLoading: boolean = true
  ) => {
    let queryParams = `?page=${page}&limit=${pageSize}`;
    if (query) queryParams += `&search=${query}`;

    const token = localStorage.getItem('token');

    await get<{
      data: {
        apps: SchoolAIData[],
        pagination?: {
          totalPages: number,
          page: number,
          total: number
        }
      },
    }, any>(
      `/third_party/get_all_apps${queryParams}`,
      { Authorization: `Bearer ${token}` },
      {
        loading: showLoading,
        onSuccess: (response) => {
          console.log('API Response:', response);
          
          // Handle different response structures
          const schoolsData: SchoolAIData[] = Array.isArray(response.data['apps']) 
            ? response.data['apps'] 
            : Array.isArray(response) 
            ? response 
            : [];
          
          setSchools(schoolsData);
          
          if (response.data.pagination) {
            setTotalPages(response.data.pagination.totalPages);
            setCurrentPage(response.data.pagination.page);
          }
        },
        onError: (err) => {
          console.error('Error fetching schools:', err);
          setSchools([]); // Set empty array on error
        }
      }
    );
  }, [get, currentPage, pageSize]);

  useEffect(() => {
    fetchSchools('', 1, true);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    fetchSchools(value, 1, false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSchools(searchQuery, page, true);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateSchool = () => {
    navigate('/home/school-ai/create');
  };

  const openMigrateModal = (school: SchoolAIData) => {
    setMigrateModal({ open: true, school });
    setMigrateForm({ fullname: '', email: '' });
    setOpenDropdownId(null);
  };

  const closeMigrateModal = () => {
    setMigrateModal({ open: false, school: null });
    setMigrateForm({ fullname: '', email: '' });
  };

  const handleMigrateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!migrateModal.school) return;

    const token = localStorage.getItem('token');
    const payload: MigrateSystemAdmin = {
      email: migrateForm.email,
      fullname: migrateForm.fullname,
      systemAppId: migrateModal.school.systemAppId || '',
    };

    setMigrateLoading(true);
    const formData = new FormData();
    formData.append('email', payload.email);
    formData.append('fullname', payload.fullname);
    formData.append('systemAppId', payload.systemAppId);

    await post(
      '/third_party/migrate_system_admin',
      formData,
      { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      {
        method: 'PUT',
        onSuccess: () => {
          setMigrateLoading(false);
          closeMigrateModal();
          fetchSchools(searchQuery, currentPage, false);
        },
        onError: () => {
          setMigrateLoading(false);
        },
      }
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">School AI</h1>
              <p className="text-gray-600 mt-1">Manage all school AI applications on the platform</p>
            </div>
            <button
              onClick={handleCreateSchool}
              className="flex items-center gap-2 px-6 py-3 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] transition-colors shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Create School
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search schools by name, email..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <LoadingStateComp Message="Schools" />
        ) : (
          <>
            {/* Schools Grid */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {schools.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Schools Found</h3>
                  <p className="text-gray-500">
                    {searchQuery ? 'Try adjusting your search criteria' : 'Get started by creating your first school'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">School</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Students</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Access Type</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {schools.map((school) => (
                        <tr key={school._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {school.appLogo ? (
                                <img
                                  src={school.appLogo}
                                  alt={school.organizationName || school.name || 'School'}
                                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-[#E55109] flex items-center justify-center text-white font-semibold">
                                  {(school.organizationName || school.name || 'S').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="font-semibold text-gray-900">{school.organizationName || school.name || 'N/A'}</div>
                                <div className="text-sm text-gray-500">{school.systemConfig?.appname || 'N/A'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{school.email || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{school.typeOfOrganization || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{school.city || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">N/A</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 rounded-full text-xs font-medium capitalize bg-blue-100 text-blue-800">
                              {school.systemConfig?.accessType || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={cn(
                              "px-3 py-1 rounded-full text-xs font-medium capitalize",
                              school.status === 'active' 
                                ? "bg-green-100 text-green-800" 
                                : school.status === 'inactive'
                                ? "bg-gray-100 text-gray-800"
                                : "bg-yellow-100 text-yellow-800"
                            )}>
                              {school.status || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="relative inline-block" ref={openDropdownId === school._id ? dropdownRef : null}>
                              <button
                                onClick={() => setOpenDropdownId(openDropdownId === school._id ? null : (school._id ?? null))}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>
                              {openDropdownId === school._id && (
                                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                  <button
                                    onClick={() => {
                                      const params = new URLSearchParams({
                                        appId: school.appID || '',
                                        name: school.organizationName || school.name || '',
                                        email: school.email || '',
                                      });
                                      navigate(`/home/school-payment-links/create?${params.toString()}`);
                                    }}
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                                  >
                                    <Link2 className="w-4 h-4" />
                                    Payment Link
                                  </button>
                                  {school.adminSwitchedBy === null && (
                                    <button
                                      onClick={() => openMigrateModal(school)}
                                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                                    >
                                      <UserCog className="w-4 h-4" />
                                      Switch Admin
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {schools.length > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Switch Admin Modal */}
      {migrateModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Switch Admin</h2>
              <button onClick={closeMigrateModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Enter the details of the new admin for <span className="font-medium text-gray-700">{migrateModal.school?.organizationName || migrateModal.school?.name}</span>.
            </p>
            <form onSubmit={handleMigrateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={migrateForm.fullname}
                  onChange={(e) => setMigrateForm((prev) => ({ ...prev, fullname: e.target.value }))}
                  placeholder="Enter full name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={migrateForm.email}
                  onChange={(e) => setMigrateForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none text-sm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeMigrateModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={migrateLoading}
                  className="flex-1 px-4 py-2.5 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] disabled:opacity-60 text-sm font-medium"
                >
                  {migrateLoading ? 'Switching...' : 'Switch Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolAIPage;
